<template>
  <v-container fluid>
    <!-- Filtering toolbox -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-chip-group v-model="selectedStatuses" column multiple>
          <v-chip filter variant="elevated" color="primary" value="to_install"> To Install </v-chip>
          <!-- <v-chip filter variant="elevated" color="warning" value="urgent">
                        Urgent
                    </v-chip> -->
          <v-chip filter variant="elevated" color="success" value="done"> Done </v-chip>
        </v-chip-group>
      </v-col>
    </v-row>

    <!-- Installation commands list -->
    <v-infinite-scroll ref="infinite" class="installation-commands-list" :height="'100%'" :items="installationCommands" @load="loadMore">
      <v-list v-if="filteredInstallations.length > 0">
        <template v-for="(installation, index) in filteredInstallations" :key="installation.id">
          <v-list-item
            :class="{
              'm-0 py-2': true,
              'done-installation': installation.isFulfilled,
            }"
          >
            <v-list-item-title class="font-weight-bold"> #{{ installation.id }} </v-list-item-title>

            <v-list-item-subtitle>
              <div class="d-flex flex-column">
                <span><strong>Client :</strong> {{ installation.clientEmail }}</span>
                <span><strong>Procédure :</strong> {{ installation.procedure.procedure }}</span>
                <span><strong>Créée le :</strong> {{ formatDate(installation.createdAt) }}</span>
                <span v-if="complete"><strong>Créée par :</strong> {{ installation.createdBy.email }}</span>
                <span v-if="complete"
                  ><strong>Installée le :</strong> {{ installation.installedAt ? formatDate(installation.installedAt) : '--' }}</span
                >
                <span v-if="complete"><strong>Installée par :</strong> {{ installation.installedBy?.email || '--' }}</span>
                <span v-if="complete"><strong>Installée sur :</strong> {{ installation.installedHeadsetSerial || '--' }}</span>
              </div>
            </v-list-item-subtitle>

            <template #append>
              <div class="d-flex flex-column align-end">
                <div v-if="installation.assignedTo.length > 0" class="mb-2">
                  <div v-for="(user, i) in installation.assignedTo" :key="i" class="d-inline ms-n2">
                    <Avatar :size="32" variant="beam" :name="user.email" />
                    <v-tooltip activator="parent" location="top">{{ user.email }}</v-tooltip>
                  </div>
                </div>
                <div v-if="installation.assignedTo.length === 0" class="mb-2">Non assigné</div>

                <div class="d-flex flex-row">
                  <v-btn class="me-2" variant="tonal" color="primary" density="comfortable" @click="installOrderCb(installation.id)">
                    <v-icon icon="mdi-upload-outline"></v-icon>
                  </v-btn>

                  <v-btn v-if="complete" class="me-2" variant="tonal" density="comfortable" @click="editOrderCb(installation.id)">
                    <v-icon icon="mdi-invoice-text-edit-outline"></v-icon>
                  </v-btn>

                  <v-btn v-if="complete" variant="tonal" color="danger" density="comfortable" @click="deleteOrderCb(installation.id)">
                    <v-icon icon="mdi-delete-outline"></v-icon>
                  </v-btn>
                </div>
              </div>
            </template>
          </v-list-item>

          <v-divider v-if="index < filteredInstallations.length - 1" class="m-0"></v-divider>
        </template>
      </v-list>

      <div v-else class="text-center py-8">
        <v-icon icon="mdi-database-off-outline" size="large" color="grey"></v-icon>
        <p class="text-grey mt-2">No installation commands match your filter criteria</p>
      </div>

      <div v-if="loading" class="text-center py-4">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
    </v-infinite-scroll>
  </v-container>

  <v-dialog v-model="editDialog" max-width="600">
    <v-card max-width="600" prepend-icon="mdi-package-variant-closed-plus" title="Modifier une commande">
      <v-card-text>
        <UpdateOrderForm :order="selectedOrder" @finished="finished" />
      </v-card-text>
      <v-card-actions>
        <v-btn @click="editDialog = false">Annuler</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import UpdateOrderForm from './forms/UpdateOrderForm.vue';
import { deleteOrder, getOrders } from '@/api/order/api';
import { Order } from '@/api/order/types';
import { format } from 'date-fns';
import { Ref, computed, ref } from 'vue';
import Avatar from 'vue-boring-avatars';
import { useRouter } from 'vue-router';

const props = defineProps<{
  complete: boolean;
}>();

const router = useRouter();

const editDialog = ref(false);
const selectedOrder = ref<number | undefined>(undefined);
const complete = ref(props.complete);
const loading = ref(false);
const page = ref(1);
const installationCommands: Ref<Array<Order>> = ref([]);
const selectedStatuses: Ref<Array<'done' | 'to_install' | 'urgent'>> = ref([]);

// Computed property to filter installations based on selected statuses
const filteredInstallations = computed(() => {
  if (selectedStatuses.value.length === 0) {
    return installationCommands.value;
  }

  return installationCommands.value.filter((installation) => {
    if (installation.isFulfilled && selectedStatuses.value.includes('done')) {
      return true;
    }
    if (!installation.isFulfilled && selectedStatuses.value.includes('to_install')) {
      return true;
    }
  });
});

// Format date
const formatDate = (date: string) => {
  return format(new Date(date), 'MMM dd, yyyy');
};

let doneCallback: ((status: 'ok' | 'empty' | 'error' | 'loading') => void) | undefined = undefined;

// Load more items when scrolling
const loadMore = async ({ done }: { done: (status: 'ok' | 'empty' | 'error' | 'loading') => void }) => {
  if (!doneCallback) {
    doneCallback = done;
  }

  const { data } = await getOrders({ page: page.value });

  if (data.length === 0) {
    done('empty');
    return;
  }

  installationCommands.value = [...installationCommands.value, ...data];
  page.value += 1;
  done('ok');
};

const refresh = async () => {
  page.value = 1;
  installationCommands.value = [];
  await loadMore({ done: doneCallback! });
};

const installOrderCb = (id: number) => {
  router.push(`/install/${id}`);
};

const editOrderCb = (id: number) => {
  selectedOrder.value = id;
  editDialog.value = true;
};

const finished = async () => {
  editDialog.value = false;
  await refresh();
};

const deleteOrderCb = async (id: number) => {
  await deleteOrder(id);
  refresh();
};

defineExpose({ refresh });
</script>

<style scoped>
.installation-commands-list {
  height: calc(100vh - 180px);
  overflow-y: auto;
}

.urgent-installation {
  background-color: rgba(244, 67, 54, 0.05);
}

.done-installation {
  background-color: rgba(76, 175, 80, 0.05);
}

.v-list-item {
  border-radius: 8px;
  margin-bottom: 4px;
  transition: background-color 0.2s ease;
}

.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
